import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import blogsData from "../data/blogsData";
import "../styles/blogList.css";

/* ─── All unique topics & categories derived from data ─── */
const ALL_TOPICS = [...new Set(blogsData.map((b) => b.topic))].sort();
const ALL_CATEGORIES = [...new Set(blogsData.map((b) => b.category))].sort();

/* ─── Category icons ─── */
const CATEGORY_META = {
  Automation: { icon: "⚙️", color: "#1a3a2a" },
  Coding: { icon: "💻", color: "#1a2540" },
  "AI Landscape": { icon: "🌐", color: "#1f1a3a" },
  "Tips & Tricks": { icon: "💡", color: "#3a1a1a" },
};

export default function Blogs() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sort, setSort] = useState("newest");
  const [topicsOpen, setTopicsOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const POSTS_PER_PAGE = 4;

  /* ─── Toggle topic selection ─── */
  const toggleTopic = (topic) =>
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );

  const clearFilters = () => {
    setSearch("");
    setSelectedTopics([]);
    setSelectedCategory("");
    setCurrentPage(1);
  };

  /* ─── Filtered + sorted posts ─── */
  const filteredPosts = useMemo(() => {
    let posts = blogsData.filter((post) => {
      const matchSearch =
        post.title.toLowerCase().includes(search.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(search.toLowerCase());
      const matchTopic =
        selectedTopics.length === 0 || selectedTopics.includes(post.topic);
      const matchCategory =
        selectedCategory === "" || post.category === selectedCategory;
      return matchSearch && matchTopic && matchCategory;
    });

    if (sort === "oldest") posts = [...posts].reverse();
    setCurrentPage(1); // reset to page 1 on filter/sort change
    return posts;
  }, [search, selectedTopics, selectedCategory, sort]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const pagedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const hasFilters =
    search || selectedTopics.length > 0 || selectedCategory !== "";

  return (
    <div className="blogs-page">
      {/* ══════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════ */}
      <section className="blog-hero">
        <div className="blog-hero-left">
          <span className="hero-badge">
            <span className="hero-badge-dot" /> AI INSIGHTS
          </span>
          <h1 className="hero-title">Blog &amp; Learning Hub</h1>
          <p className="hero-subtitle">
            Deep dives into AI tools, trend round-ups, use-case guides, and
            behind-the-scenes stories from the community. Articles designed to
            be helpful, not hype.
          </p>
          <div className="hero-cta">
            <a href="#blog-grid" className="cta-primary">
              Browse Articles
            </a>
            <a
              href="/blogs"
              className="cta-outline"
            >
              Explore Tutorials
            </a>
          </div>
        </div>

        <div className="blog-hero-right">
          {Object.entries(CATEGORY_META).map(([cat, meta]) => (
            <button
              key={cat}
              className={`hero-cat-card${
                selectedCategory === cat ? " active" : ""
              }`}
              style={{ "--cat-bg": meta.color }}
              onClick={() =>
                setSelectedCategory(selectedCategory === cat ? "" : cat)
              }
            >
              <span className="hero-cat-icon">{meta.icon}</span>
              <span className="hero-cat-label">{cat}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          FILTER + GRID SECTION
      ══════════════════════════════════════════ */}
      <section className="blog-body" id="blog-grid">
        {/* LEFT SIDEBAR */}
        <aside className="blog-sidebar">
          <div className="sidebar-section">
            <div
              className="sidebar-heading"
              onClick={() => setTopicsOpen((o) => !o)}
            >
              <span>Topics</span>
              <span className={`chevron${topicsOpen ? " open" : ""}`}>▼</span>
            </div>

            {topicsOpen && (
              <div className="topic-list">
                {ALL_TOPICS.map((topic) => (
                  <label key={topic} className="topic-item">
                    <input
                      type="checkbox"
                      checked={selectedTopics.includes(topic)}
                      onChange={() => toggleTopic(topic)}
                    />
                    <span className="topic-label">{topic}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <div className="sidebar-heading">
              <span>Categories</span>
            </div>
            <div className="topic-list">
              {ALL_CATEGORIES.map((cat) => (
                <label key={cat} className="topic-item">
                  <input
                    type="radio"
                    name="category"
                    checked={selectedCategory === cat}
                    onChange={() =>
                      setSelectedCategory(
                        selectedCategory === cat ? "" : cat
                      )
                    }
                  />
                  <span className="topic-label">{cat}</span>
                </label>
              ))}
            </div>
          </div>

          {hasFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              ✕ Clear All Filters
            </button>
          )}
        </aside>

        {/* RIGHT CONTENT */}
        <div className="blog-main">
          {/* Search + Sort bar */}
          <div className="blog-toolbar">
            <div className="search-wrap">
              <span className="search-icon">🔍</span>
              <input
                className="search-input"
                placeholder="Search blog posts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="sort-wrap">
              <span className="sort-label">SORT</span>
              <select
                className="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>

            <span className="post-count">
              Showing{" "}
              <strong>
                {Math.min((currentPage - 1) * POSTS_PER_PAGE + 1, filteredPosts.length)}
                –{Math.min(currentPage * POSTS_PER_PAGE, filteredPosts.length)}
              </strong>{" "}
              of{" "}
              <strong className="accent">{filteredPosts.length}</strong> posts
            </span>
          </div>

          {/* Blog Grid */}
          {filteredPosts.length === 0 ? (
            <div className="no-results">
              <span>🔎</span>
              <p>No posts match your filters.</p>
              <button onClick={clearFilters}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="blog-grid">
                {pagedPosts.map((post) => (
                  <BlogCard key={post.id} post={post} navigate={navigate} />
                ))}
              </div>

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-controls">
                    <button
                      className="page-btn prev-btn"
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      ‹ Previous
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`page-btn page-num${currentPage === page ? " active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      className="page-btn next-btn"
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next ›
                    </button>
                  </div>

                  <p className="page-label">Page {currentPage} of {totalPages}</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/* ─── Blog Card Component ─── */
function BlogCard({ post, navigate }) {
  const [liked, setLiked] = useState(false);

  return (
    <article className="blog-card">
      {/* Cover Image */}
      <div className="card-image-wrap">
        <img
          src={post.image}
          alt={post.title}
          className="card-image"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        {/* Like button */}
        <button
          className={`card-like-btn${liked ? " liked" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setLiked((l) => !l);
          }}
          aria-label="Like post"
        >
          ♥
        </button>
      </div>

      {/* Tags */}
      <div className="card-tags">
        <span className="card-tag">{post.topic}</span>
        {post.category !== post.topic && (
          <span className="card-tag">{post.category}</span>
        )}
      </div>

      {/* Body */}
      <div className="card-body">
        <h2 className="card-title">{post.title}</h2>

        <div className="card-meta">
          <span className="card-author">✍️ {post.author}</span>
          <span className="card-date">📅 {post.date}</span>
        </div>

        <p className="card-excerpt">{post.excerpt}</p>
      </div>

      {/* Footer CTA */}
      <span
        className="card-read-btn"
        onClick={() => navigate(`/blog/${post.id}`)}
      >
        Read Article →
      </span>
    </article>
  );
}